package dev.lingvo.site.pages.app.projects.slug.keys

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.BulkKeyOp
import dev.lingvo.shared.BulkKeysRequest
import dev.lingvo.shared.DeletedKeyDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LArchiveRestoreIcon
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBreadcrumbItem
import dev.lingvo.site.ds.LBreadcrumbs
import dev.lingvo.site.ds.LBulkActionBar
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTableSelectAllHeaderCell
import dev.lingvo.site.ds.LTableSelection
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.pages.app.projects.slug.bulkReceipt
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectAction
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** The id the select-all header carries, so clearing a selection has somewhere to put focus back. */
private const val DELETED_SELECT_ALL_ID = "deleted-keys-select-all"

/**
 * The keys this project deleted, and the way to bring one back.
 *
 * **Deleting a key has never destroyed anything.** `KeyRepo.softDelete` sets `deleted_at` and every
 * translation stays attached to the row, which is the right design and was invisible: nothing in the
 * product could reach a deleted key, so a reader who removed the wrong string experienced exactly
 * what they would have experienced if the delete had been real. The reassurance existed only in the
 * schema. This is the page that makes it true for a person.
 *
 * **Behind the same gate as the delete that put them here.** Restoring changes what the repository's
 * files contain on the next pull, which is not a translator's decision — and the control is absent
 * for anybody else rather than offered and refused.
 *
 * **`RESTORE` takes ids, never a filter.** `BulkKeysFilter` resolves through the same predicate
 * builder `GET /keys` uses, which sees live keys only, so a filter form of a restore would
 * consistently match nothing. The selection here is always the rows on screen.
 *
 * Re-adding a key under a deleted one's name does not need this page: `KeyRepo.create` already
 * resurrects the original rather than inserting a second row, so its translations come back with it.
 * This is for the case nobody re-added — a string deleted by mistake, noticed later.
 */
@Page("/app/projects/{slug}/keys/deleted")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProjectDeletedKeysPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    val scope = rememberCoroutineScope()
    val verifiedRoute = liveProjectRoute(slug)
    val authority = ProjectAuthority.resolve(verifiedRoute?.project, Session.organizations)
    val structureClaim = ProjectActionClaim.capture(
        verifiedRoute,
        Session.organizations,
        ProjectPrivilege.STRUCTURE,
    )
    val canRestore = authority.canEditStructure && structureClaim != null
    var refreshKey by remember { mutableStateOf(0) }
    var restoring by remember { mutableStateOf<String?>(null) }
    var bulkBusy by remember { mutableStateOf(false) }
    var selected by remember(slug) { mutableStateOf<Set<String>>(emptySet()) }
    var message by remember { mutableStateOf<String?>(null) }
    var receipt by remember { mutableStateOf<String?>(null) }
    val restoreOne: suspend (String, String) -> Unit = ApiClient::restoreKey
    val restoreBulk: suspend (String, BulkKeysRequest) -> dev.lingvo.shared.BulkKeysResponse = ApiClient::keysBulk
    // The slug identifies the subject and still blanks when it changes; the restore counter only
    // re-runs the request. Passing it as a key blanked the table on a successful restore and took
    // the focused button with it, which is the one moment the reader most wants to stay put.
    //
    // **The whole `Reloadable`, not just its state.** Taking `.state` threw away `refreshing` and
    // `refreshError`, and a bulk restore ends with `refreshKey++` under a green receipt: when that
    // re-read failed, the page said "12 keys restored" over a table still listing all twelve as
    // deleted, with nothing anywhere saying the list was stale. The reader reads the two as
    // contradicting each other and restores them a second time.
    val reloadable = loadReloadable(slug, reloadToken = refreshKey) { ApiClient.deletedKeys(slug) }
    val state = reloadable.state

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            title = t("app.projects.slug.keys.deleted.deleted_strings", "Deleted keys"),
            subtitle = t("app.projects.slug.keys.deleted.deleting_a_key_here_never_removed_2", "Deleting a key here never removed its translations. Bring one back and they come " +
                "back with it."),
            // The trail, not a bare "Back to keys" link: this page is two levels down and the link
            // named only one of them.
            eyebrow = {
                LBreadcrumbs(
                    listOf(
                        LBreadcrumbItem(
                            t("app.projects.slug.keys.deleted.keys", "Keys"),
                            "/app/projects/$slug/keys",
                        ),
                        LBreadcrumbItem(t("app.projects.slug.keys.deleted.deleted_strings", "Deleted keys")),
                    ),
                )
            },
        )

        // Only ever written by a failed restore, so it announces itself.
        message?.let {
            LAlert(
                t("app.projects.slug.keys.deleted.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }
        // The receipt for a batch: what the server actually put back, in its own words.
        receipt?.let {
            LAlert(
                t("app.projects.slug.keys.deleted.restored", "Restored"),
                it,
                variant = LAlertVariant.SUCCESS,
                live = true,
                onDismiss = { receipt = null },
            )
        }

        LAsyncContent(
            state,
            rows = 5,
            loadingLabel = t("app.projects.slug.keys.deleted.loading_deleted_strings", "Loading deleted keys"),
            subject = t("app.projects.slug.keys.deleted.deleted_strings_2", "deleted keys"),
            onRetry = { refreshKey++ },
        ) { loaded ->
            DeletedKeysContent(
                keys = loaded.keys,
                canRestore = canRestore,
                restoringId = restoring,
                bulkBusy = bulkBusy,
                refreshing = reloadable.refreshing,
                refreshError = reloadable.refreshError?.message,
                selected = selected,
                onSelectedChange = { id, checked ->
                    selected = if (checked) selected + id else selected - id
                },
                onSelectAll = { everything ->
                    selected = if (everything) loaded.keys.map(DeletedKeyDto::id).toSet() else emptySet()
                },
                onRestoreOne = { key ->
                    val claim = structureClaim ?: return@DeletedKeysContent
                    scope.launch {
                        restoring = key.id
                        try {
                            requestProjectAction(
                                claim,
                                { liveProjectRoute(slug) },
                                { Session.organizations },
                            ) { restoreOne(slug, key.id) }
                            message = null
                            selected = selected - key.id
                            refreshKey++
                        } catch (cancelled: CancellationException) {
                            throw cancelled
                        } catch (failure: Throwable) {
                            message = failure.safeMessage(
                                tr(
                                    "app.projects.slug.keys.deleted.couldnt_restore_that_key",
                                    "Couldn't restore that key.",
                                ),
                            )
                        } finally {
                            restoring = null
                        }
                    }
                },
                onRestoreSelected = { ids ->
                    val claim = structureClaim ?: return@DeletedKeysContent
                    if (ids.isEmpty()) return@DeletedKeysContent
                    scope.launch {
                        bulkBusy = true
                        try {
                            val response = requestProjectAction(
                                claim,
                                { liveProjectRoute(slug) },
                                { Session.organizations },
                            ) {
                                // **Ids, never the filter form.** `BulkKeysFilter` resolves through
                                // the predicate builder `GET /keys` uses, which sees live keys only,
                                // so a filter-form restore would consistently match nothing.
                                restoreBulk(slug, BulkKeysRequest(op = BulkKeyOp.RESTORE, keyIds = ids))
                            }
                            message = null
                            receipt = bulkReceipt(BulkKeyOp.RESTORE, response)
                            selected = emptySet()
                            refreshKey++
                        } catch (cancelled: CancellationException) {
                            throw cancelled
                        } catch (failure: Throwable) {
                            message = failure.safeMessage(
                                tr(
                                    "app.projects.slug.keys.deleted.couldnt_restore_those_keys",
                                    "Couldn't restore those keys.",
                                ),
                            )
                        } finally {
                            bulkBusy = false
                        }
                    }
                },
            )
        }
    }
}

/**
 * The table, the selection, and the one control that puts a batch back.
 *
 * Separate from the page so a test can mount exactly what a reader sees without a router, a session
 * and a project route: the authority boundary above is exercised by `ProjectKeysAuthorityBrowserTest`,
 * and what belongs here is whether the strip says the right count and hands the right ids up.
 *
 * @param refreshing the list is being re-read, usually because a restore just landed. The bulk
 *   control that started it is the thing that stays busy, so the receipt and the table cannot be
 *   read as settled while the table is still the old one.
 * @param refreshError a failed re-read over rows that are still on screen. Rendered beside them
 *   rather than replacing them: the rows are the last thing the server actually said.
 */
@Composable
internal fun DeletedKeysContent(
    keys: List<DeletedKeyDto>,
    canRestore: Boolean,
    restoringId: String?,
    bulkBusy: Boolean,
    refreshing: Boolean,
    refreshError: String?,
    selected: Set<String>,
    onSelectedChange: (String, Boolean) -> Unit,
    onSelectAll: (Boolean) -> Unit,
    onRestoreOne: (DeletedKeyDto) -> Unit,
    onRestoreSelected: (List<String>) -> Unit,
) {
    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
        // Beside the last good rows, never instead of them, and announced: the reader is looking at
        // a receipt for a restore whose result this table has not caught up with yet.
        refreshError?.let {
            LAlert(
                t(
                    "app.projects.slug.keys.deleted.couldnt_refresh_this_list",
                    "Couldn't refresh this list",
                ),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }
        val selection = when {
            keys.isEmpty() || keys.none { it.id in selected } -> LTableSelection.NONE
            keys.all { it.id in selected } -> LTableSelection.ALL
            else -> LTableSelection.SOME
        }
        if (keys.isEmpty()) {
            LEmptyState(
                t("app.projects.slug.keys.deleted.nothing_deleted", "Nothing deleted"),
                t("app.projects.slug.keys.deleted.keys_removed_from_this_project_would_2", "Keys removed from this project would be listed here, with the " +
                    "translations waiting to come back with them."),
                Modifier.fillMaxWidth(),
                LEmptyStateIcon.AUDIT,
            ) {}
        } else {
            LTable(caption = t("app.projects.slug.keys.deleted.deleted_strings_3", "Deleted keys")) {
                LTableHead {
                    LTableRow {
                        if (canRestore) {
                            LTableSelectAllHeaderCell(
                                state = selection,
                                onChange = onSelectAll,
                                label = t("app.projects.slug.keys.deleted.select_every_deleted_key", "Select every deleted key"),
                                modifier = Modifier.attr("id", DELETED_SELECT_ALL_ID),
                            )
                        }
                        listOf(
                            t("app.projects.slug.keys.deleted.key", "Key"),
                            t("app.projects.slug.keys.deleted.file", "File"),
                            t("app.projects.slug.keys.deleted.deleted", "Deleted"),
                            t("app.projects.slug.keys.deleted.translations", "Translations"),
                            // Named rather than blank. The column holds a Restore button on
                            // every row, and an empty `th` leaves those buttons in a column
                            // a screen reader cannot announce the purpose of.
                            t("app.projects.slug.keys.deleted.actions", "Actions"),
                        ).forEach { LTableHeaderCell { Text(it) } }
                    }
                }
                LTableBody {
                    keys.forEach { key ->
                        DeletedKeyRow(
                            key = key,
                            selectable = canRestore,
                            selected = key.id in selected,
                            onSelectedChange = { checked -> onSelectedChange(key.id, checked) },
                            // **Scoped to the row.** Every button on the page used to go busy
                            // together, so restoring one key disabled the other eleven and the
                            // reader could not queue a second decision.
                            busy = bulkBusy,
                            restoring = restoringId == key.id,
                            onRestore = { onRestoreOne(key) },
                        )
                    }
                }
            }
            LMuted(
                t(
                    "app.projects.slug.keys.deleted.only_the_most_recent_are_kept",
                    "The most recent deletions are listed. A restored key reappears in " +
                        "the keys list and in the next pull.",
                ),
            )
        }
        if (canRestore) {
            LBulkActionBar(
                selectedCount = selected.size,
                onClear = { onSelectAll(false) },
                label = tPlural(
                    "app.projects.slug.keys.deleted.count.selected",
                    selected.size,
                    "{count} deleted key selected",
                    "{count} deleted keys selected",
                ),
                returnFocusToId = DELETED_SELECT_ALL_ID,
            ) {
                LButton(
                    t("app.projects.slug.keys.deleted.restore_selected", "Restore selected"),
                    { onRestoreSelected(selected.toList()) },
                    variant = LButtonVariant.PRIMARY,
                    // The re-read that follows a batch belongs to this control: it is not finished
                    // until the table it changed has been read back.
                    enabled = !bulkBusy && !refreshing && restoringId == null,
                    loading = bulkBusy || refreshing,
                    leadingIcon = { LArchiveRestoreIcon() },
                )
            }
        }
    }
}

/**
 * The server's sentence, or this page's own — never an internal one.
 *
 * Cast first, the way `Keys.kt` and `Glossary.kt` do: a `check`/`require` failure inside a
 * mutation lambda is a `Throwable` with a message written for a developer, and the unguarded form
 * put "Required value was null." in front of the reader.
 */
private fun Throwable.safeMessage(fallback: String): String =
    (this as? ApiException)?.message?.takeIf(String::isNotBlank) ?: fallback

@Composable
private fun DeletedKeyRow(
    key: DeletedKeyDto,
    selectable: Boolean,
    selected: Boolean,
    onSelectedChange: (Boolean) -> Unit,
    busy: Boolean,
    restoring: Boolean,
    onRestore: () -> Unit,
) {
    LTableRow(
        selected = selected,
        selectable = selectable,
        onSelectedChange = onSelectedChange,
        selectionLabel = tf(
            "app.projects.slug.keys.deleted.select_named_key",
            "Select {name}",
            "name" to key.name,
        ),
    ) {
        LTableHeaderCell(scope = LTableHeaderScope.ROW) { Span(Modifier.monoFont().toAttrs()) { Text(key.name) } }
        LTableCell { Span(Modifier.monoFont().toAttrs()) { Text(key.filePath) } }
        LTableCell { Text(formatUtc(key.deletedAt, LTimeStyle.AGE, "—")) }
        // The number this page exists for: a reader deciding whether to restore is deciding whether
        // somebody's work is on the other side of the button, and the key name alone does not say.
        LTableCell {
            Text(
                if (key.translations == 0) {
                    tr("app.projects.slug.keys.deleted.none", "None")
                } else {
                    tPlural(
                        "app.projects.slug.keys.deleted.count.translation",
                        key.translations,
                        "{count} translation",
                        "{count} translations",
                    )
                },
            )
        }
        LTableCell {
            if (selectable) {
                LButton(
                    t("app.projects.slug.keys.deleted.restore", "Restore"),
                    onRestore,
                    variant = LButtonVariant.SECONDARY,
                    size = LButtonSize.SM,
                    // Only this row's control goes busy: a batch in flight locks every row, a
                    // single restore locks its own.
                    enabled = !busy && !restoring,
                    loading = restoring,
                    // Every row's button says "Restore"; without this a screen reader hears it once
                    // per deleted string with nothing to tell them apart.
                    accessibleLabel = tf(
                        "app.projects.slug.keys.deleted.restore_named_key",
                        "Restore {name}",
                        "name" to key.name,
                    ),
                    leadingIcon = { LArchiveRestoreIcon() },
                )
            }
        }
    }
}
